<template>
    <form-modal
        ref="formModal"
        :title="getI18nModified({ prefix: 'COMMON.filter', modifier: 2 })"
        @handleHide="handleHide"
    >
        <template v-slot:form>
            <form @submit.prevent="handleOk">
                <b-form-row>
                    <b-col md="12">
                        <b-form-group
                            :label="i18nAction"
                            label-for="filter-action"
                            class="label-pdv"
                        >
                            <multiselect
                                v-model="filterModal.action"
                                :options="actionOptions"
                                track-by="value"
                                label="text"
                                :searchable="false"
                                :show-labels="false"
                                :multiple="false"
                                :allow-empty="true"
                                :placeholder="i18nAction"
                            />
                        </b-form-group>
                    </b-col>

                    <b-col md="12">
                        <b-form-group
                            :label="i18nDate"
                            label-for="filter-date"
                            class="label-pdv"
                        >
                            <!-- ✅ forma universal (independente de .sync) -->
                            <date-picker
                                id="filter-date"
                                :value="filterModal.date"
                                :disable-clean-button="false"
                                @input="filterModal.date = $event"
                            />
                        </b-form-group>
                    </b-col>
                </b-form-row>

                <b-form-row>
                    <b-col md="12">
                        <b-form-group
                            :label="i18nFilterType"
                            label-for="filter-filter-type"
                            class="label-pdv"
                        >
                            <multiselect
                                v-model="filterModal.filter_type"
                                :options="filterTypeOptions"
                                track-by="value"
                                label="text"
                                :searchable="false"
                                :show-labels="false"
                                :multiple="false"
                                :allow-empty="true"
                                :placeholder="i18nFilterType"
                            />
                        </b-form-group>
                    </b-col>
                </b-form-row>
            </form>
        </template>

        <template v-slot:footer>
            <save-button
                ref="okButton"
                :busy="submitLoading"
                :disabled="submitLoading"
                :title="getI18n('ACTION.filter')"
                @onClick="handleOk"
            />
            <clear-filter-button
                @onClick="handleClearFilter"
            />
        </template>
    </form-modal>
</template>

<script>
import swalFeedback from '@src/mixins/swalFeedback.vue'
import validationMixin from '@src/mixins/validation.vue'
import FormModal from '@/src/components/FormModal.vue'
import fieldsValidation from '@src/mixins/fieldsValidation.vue'
import Multiselect from 'vue-multiselect'
import DatePicker from '@src/components/DatePicker'
import SaveButton from '@/src/components/SaveButton'
import ClearFilterButton from '@/src/components/ClearFilterButton'
import cloneObject from '@utils/clone-object'
import { SCHEDULE_ACTION_VALUES } from '@src/constants/scheduleActions'

const i18nKey = 'SCHEDULES'
const i18nCommon = 'COMMON'

const emptyFilter = () => ({
    action: null,
    date: null,
    filter_type: null,
})

export default {
    components: {
        FormModal,
        Multiselect,
        DatePicker,
        SaveButton,
        ClearFilterButton,
    },
    mixins: [validationMixin, swalFeedback, fieldsValidation],
    props: {
        handleSubmit: {
            type: Function,
            required: true,
            default: function () {},
        },
    },
    data() {
        return {
            i18nKey,
            i18nCommon,

            // ✅ sempre com as chaves reativas
            filterModal: emptyFilter(),

            submitLoading: false,
            actionOptions: [],
            filterTypeOptions: [],
        }
    },
    validations: {},
    computed: {
        i18nAction() {
            return this.getI18n(i18nKey, 'FIELDS.action')
        },
        i18nDate() {
            return this.getI18n(i18nKey, 'FIELDS.date')
        },
        i18nFilterType() {
            return this.getI18n(i18nKey, 'FIELDS.filter_type')
        },
    },
    created() {
        this.actionOptions = SCHEDULE_ACTION_VALUES.map((value) => ({
            value,
            text: this.getI18n(i18nKey, `ACTIONS.${value}`),
        }))
        this.filterTypeOptions = [
            { value: 'promotor', text: this.getI18n(i18nCommon, 'promotor') || 'Promotor' },
            { value: 'supervisor', text: this.getI18n(i18nCommon, 'supervisor') || 'Supervisor' },
            { value: 'regional', text: this.getI18n('POS', 'TITLES.regional') || 'Regional' },
        ]
    },
    methods: {
        showModal(previousFilters) {
            // previousFilters pode vir como string (recomendado)
            // ou como objeto (caso legado) — vamos suportar ambos
            const prev = cloneObject(previousFilters || {})
            this.filterModal = {
                ...emptyFilter(),
                ...prev,
            }

            this.$nextTick(() => {
                // action: string -> objeto
                if (this.filterModal.action) {
                    if (typeof this.filterModal.action === 'string') {
                        this.filterModal.action =
                            this.actionOptions.find(opt => opt.value === this.filterModal.action) || null
                    } else if (typeof this.filterModal.action === 'object' && this.filterModal.action.value) {
                        this.filterModal.action =
                            this.actionOptions.find(opt => opt.value === this.filterModal.action.value) || null
                    }
                } else {
                    this.filterModal.action = null
                }

                // filter_type: string -> objeto
                if (this.filterModal.filter_type) {
                    if (typeof this.filterModal.filter_type === 'string') {
                        this.filterModal.filter_type =
                            this.filterTypeOptions.find(opt => opt.value === this.filterModal.filter_type) || null
                    } else if (typeof this.filterModal.filter_type === 'object' && this.filterModal.filter_type.value) {
                        this.filterModal.filter_type =
                            this.filterTypeOptions.find(opt => opt.value === this.filterModal.filter_type.value) || null
                    }
                } else {
                    this.filterModal.filter_type = null
                }

                // date: garante existir
                if (!this.filterModal.date) {
                    this.filterModal.date = null
                }
            })

            this.submitLoading = false
            this.$refs.formModal.show()
        },

        cleanModal() {
            this.filterModal = emptyFilter()
        },

        handleHide() {
            if (!this.submitLoading) {
                this.cleanModal()
            }
        },

        handleOk() {
            if (this.submitLoading) return
            this.submitLoading = true

            // ✅ enviar para fora SEMPRE em formato estável (string value)
            const payload = {
                ...this.filterModal,
                action: this.filterModal.action?.value || null,
                filter_type: this.filterModal.filter_type?.value || null,
            }

            this.$props.handleSubmit(payload)
            this.$refs.formModal.hide()
            this.submitLoading = false
        },

        async handleClearFilter() {
            this.cleanModal()
        },
    },
}
</script>
